import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteInsuranceCompany, getInsuranceCompanies, getInsuranceCompaniesDetails, GetInsuranceCompaniesInput, getInsuranceCompaniesStates, getInsuranceCompaniesSupportedByProviderAndPatient, updateInsuranceCompany, UpdateInsuranceCompanyInput } from '@uplift/api';
import { BaseQueryOpts } from './baseTypes';


export const useInsuranceCompaniesQuery = (data: GetInsuranceCompaniesInput = {}, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'insuranceCompanies', data ],
    queryFn: async () => {
      const insuranceCompanies = await getInsuranceCompanies(data);

      return insuranceCompanies.data;
    },
    ...opts,
  });
};


export const useUpdateInsuranceCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...values }: UpdateInsuranceCompanyInput & { id: string }) => {
      await updateInsuranceCompany(id, values);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([ 'insuranceCompanies' ]),
      ]);
    },
  });
};

export const useDeleteInsuranceCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await deleteInsuranceCompany(id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([ 'insuranceCompanies' ]),
      ]);
    },
  });
};


export const useInsuranceCompaniesStatesQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'insuranceCompanies', 'states' ],
    queryFn: async() => {
      const insuranceCompanies = await getInsuranceCompaniesStates();

      return insuranceCompanies.data;
    },
    ...opts,
  });
};


export const useInsuranceCompaniesDetailsQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'insuranceCompanies', 'details' ],
    queryFn: getInsuranceCompaniesDetails,
    select: response => response.data.reduce((acc, company) => ({ ...acc, [ company.insuranceCompanyId ]: company }), {}),
    ...opts,
  });
};


export const useInsuranceCompaniesSupportedByProviderAndPatientQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'provider', providerId, 'insuranceCompanies' ],
    queryFn: async () => {
      const response = await getInsuranceCompaniesSupportedByProviderAndPatient(providerId);

      return response.data;
    },
    ...opts,
  });
};
